import { LogOut, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { getUserDisplayName, getUserInitials, getUserRoleLabel } from "./user-display";

interface UserMenuProps {
  theme?: "default" | "on-dark";
  className?: string;
}

export function UserMenu({ theme = "default", className }: UserMenuProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = getUserDisplayName(user);
  const roleLabel = getUserRoleLabel(user?.role);
  const initials = getUserInitials(user);
  const onDark = theme === "on-dark";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex h-11 items-center gap-3 rounded-2xl border border-border/60 bg-background/80 px-3 outline-none shadow-sm transition-colors sm:gap-3.5 sm:pl-4 sm:pr-2",
          onDark ? "border-white/15 hover:bg-white/10" : "hover:border-primary/20 hover:bg-muted/40",
          className,
        )}
      >
        <div className="hidden min-w-0 flex-col items-end sm:flex">
          <span
            className={cn(
              "max-w-[180px] truncate text-[15px] font-semibold leading-tight",
              onDark ? "text-white" : "text-foreground",
            )}
          >
            {displayName}
          </span>
          <span
            className={cn(
              "max-w-[180px] truncate text-xs leading-tight",
              onDark ? "text-white/70" : "text-muted-foreground",
            )}
          >
            {roleLabel}
          </span>
        </div>
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
            onDark
              ? "bg-white/15 text-white ring-1 ring-white/25"
              : "bg-primary text-primary-foreground ring-2 ring-primary/15",
          )}
        >
          {initials}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-72 rounded-xl border border-border p-0 shadow-lg">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>
              <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
              {user?.email && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground/80">{user.email}</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-1.5">
          <DropdownMenuItem
            onClick={() => navigate("/settings")}
            className="h-9 cursor-pointer rounded-lg text-sm font-medium"
          >
            <Settings className="mr-2.5 h-4 w-4 text-muted-foreground" />
            Settings
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="my-0" />
        <div className="p-1.5">
          <DropdownMenuItem
            onClick={() => logout()}
            className="h-9 cursor-pointer rounded-lg text-sm font-medium text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="mr-2.5 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
